/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {CTCEntity, Entity} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {IonSnackbar} from '../notifications/IonSnackbar';
import {partialStatusMessageFormatter, translatePromptAction} from '../utils/ComponentUtils';
import DeletePrompt from './DeletePrompt';

// defines the BulkDeletePrompt prop type definitions ...
export interface BulkDeletePromptProps {
  show: boolean;
  entity?: Entity | string;
  items: CTCEntity[] | null;
  result?: CTCEntity[] | null;
  loading?: boolean;
  isReadOnlyUser?: boolean;
  error?: ServiceError | null;
  overrideMessage?: Function | null;
  onDeleteConfirm: (items: CTCEntity[]) => void;
  onDeleteCancel: () => void;
  deletedItems: CTCEntity[] | [];
  title?: string;
  actionLabel?: string;
};

/**
 * @BulkDeletePrompt
 *
 * The BulkDeletePrompt provides the UI for prompting, confirming
 * or cancelling bulk delete action.
 *
 * ```typescript
 *
 *    import {BulkDeletePrompt} from '@ion/dls/src/editors/';
 *
 *    return (
 *      <BulkDeletePrompt show={bulkDeleteIsEditing}
 *        entity={translate('common:entityLabels.notifications')}
 *        onDeleteConfirm={onBulkDeleteConfirm}
 *        onDeleteCancel={onBulkDeleteCancel}
 *        items={bulkDeleteSelected}
 *        result={bulkDeleteResult}
 *        loading={bulkDeleteLoading}
 *        error={bulkDeleteError} />
 *    );
 *
 * ```
 *
 * @param {boolean}       show When true, the bulk delete prompt will be displayed.
 * @param {CTCEntity[]}   items The selected items (CTC Entity) array that is to be deleted.
 * @param {CTCEntity[]}   result The CTC Entity array returned by the service after it has been deleted.
 * @param {boolean}       loading When true, indicates that the CTC Entity array is currently being
 *                        deleted.
 * @param {ServiceError}  error A reference to the ServiceError If the deletion fails.
 * @param {Function}      onDeleteConfirm callback function which will be invoked with the
 *                        CTC Entity array when the user confirms the deletion.
 * @param {Function}      onDeleteCancel callback function which will be invoked if the user
 *                        cancels the delete operation.
 */
export const BulkDeletePrompt = ({
  show,
  items,
  entity,
  result,
  loading,
  error,
  onDeleteConfirm,
  onDeleteCancel,
  deletedItems,
  overrideMessage = null,
  isReadOnlyUser = false,
  title,
  actionLabel
}: BulkDeletePromptProps) => {
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (error) {
      setShowError(true);
    }
  }, [error]);

  useEffect(() => {
    if (result && !loading) {
      setShowSuccess(true);
    }
  }, [result]);

  function onClose() {
    setShowError(false);
    setShowSuccess(false);
  }
  if (show || showError || showSuccess) {
    return (
      <>
        {show && (
          <DeletePrompt show
            bulkDelete
            entity={entity}
            items={items}
            isLoading={loading}
            overrideMessage={overrideMessage}
            title={title}
            actionLabel={actionLabel}
            onConfirm={function() {
              onDeleteConfirm(items);
            }}
            onCancel={function() {
              onDeleteCancel();
            }} />
        )}
        {showError && (
          <IonSnackbar show
            message={error?.message || translate('common:editors.delete.failure', {
              entity
            })}
            actionLabel={translatePromptAction('notifications:warning.actionLabel', isReadOnlyUser)}
            onAction={onClose} />
        )}
        {showSuccess && !showError && (
          <IonSnackbar show
            message={translate('common:editors.delete.success', {
              entity
            })}
            partialSuccessState={partialStatusMessageFormatter(deletedItems, items)}
            actionLabel={translate('common:editors.delete.ok')}
            onAction={onClose}
            onClose={onClose}
            autoHideDuration={3000} />
        )}
      </>
    );
  };
  return null;
};

export default BulkDeletePrompt;
